import React, { useState, useEffect, useCallback, KeyboardEvent, createRef } from "react";

import clsx from "clsx";

import { TextArea, Link } from "@plex/react-components";

import { commonMessages } from "infrastructure/localization";
import { useLocalizedValues } from "ui/hooks";

import styles from "./InlineEditPopup.module.scss";

export interface IInlineEditPopupProps {
  initialValue?: string;
  originalValue?: string;
  className?: string;
  onValueChanged?: (value?: string) => void;
  onEditPopupClosed: () => void;
  onValueCommited: () => void;
}

export const InlineEditPopup: React.FC<IInlineEditPopupProps> = ({ initialValue, originalValue, onValueChanged, onEditPopupClosed, onValueCommited, className }) => {
  const [textValue, setTextValue] = useState("");
  const localizedValues = useLocalizedValues(() => {
    return {
      resetActionText: commonMessages.action("termTranslationsReset")
    };
  });

  const textAreaRef = createRef<HTMLTextAreaElement>();

  useEffect(() => {
    onValueChanged && onValueChanged(textValue);
  }, [textValue, onValueChanged]);

  useEffect(() => {
    setTextValue(initialValue || "");
  }, [initialValue]);

  const onResetLinkMouseDown = e => {
    e.preventDefault();

    if (e.button === 0) {
      //originalValue is undefined means that it was not changed and value should be reset to last value before edit (initial value)
      setTextValue(originalValue === undefined ? initialValue || "" : originalValue || "");
    }
  };

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.scrollTop = textAreaRef.current.scrollHeight;
    }
  }, [textAreaRef]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Esc") {
        onEditPopupClosed();
      } else if (e.ctrlKey && e.key === "Enter") {
        onValueCommited();
      }
    },
    [onEditPopupClosed, onValueCommited]
  );

  return (
    <div className={clsx(styles.base, className)}>
      <TextArea value={textValue} onChange={setTextValue} onBlur={onValueCommited} onKeyDown={onKeyDown} ref={textAreaRef} autoFocus />
      <div className={styles.resetLink}>
        <Link {...{ tabIndex: -1, onMouseDown: onResetLinkMouseDown }}>{localizedValues.resetActionText}</Link>
      </div>
    </div>
  );
};
